import Phaser from 'phaser';

/**
 * TransitionScene - Handles the perspective transformation
 * 
 * Creates the illusion of the room "rotating" from top-down (3/4 view)
 * to side-scrolling (platformer view) when entering a boss battle.
 * 
 * Animation sequence:
 * 1. Zoom in on the target (bed)
 * 2. Squash/stretch to simulate perspective rotation
 * 3. Cross-fade to side-view background
 * 4. Reveal boss scene
 */

interface TransitionData {
  fromScene: string;
  toScene: string;
  roomData?: {
    x: number;
    y: number;
    roomId: number;
  };
}

export class TransitionScene extends Phaser.Scene {
  private transitionData!: TransitionData;
  
  constructor() {
    super('TransitionScene');
  }

  init(data: TransitionData) {
    this.transitionData = data || {
      fromScene: 'HouseScene',
      toScene: 'BlanketBossScene',
    };
  }

  create() {
    const { width, height } = this.scale;
    
    // Stop the previous scene
    if (this.transitionData.fromScene) {
      this.scene.stop(this.transitionData.fromScene);
    }

    // Create the transformation effect
    this.createTransformationEffect(width, height);
  }

  private createTransformationEffect(width: number, height: number) {
    // Phase 1: Dark overlay with zoom effect
    const overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000, 0);
    overlay.setDepth(100);

    // Create a "top-down" representation (simplified room)
    const topDownRoom = this.createTopDownRepresentation(width, height);
    topDownRoom.setDepth(50);

    // Create the side-view background (will fade in)
    let sideViewBg: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
    if (this.textures.exists('emily_bedroom_side')) {
      sideViewBg = this.add.image(width / 2, height / 2, 'emily_bedroom_side');
      sideViewBg.setDisplaySize(width, height);
    } else {
      // Fallback: gradient rectangle
      sideViewBg = this.add.rectangle(width / 2, height / 2, width, height, 0x4a2040);
    }
    sideViewBg.setAlpha(0);
    sideViewBg.setDepth(40);

    // Text indicator
    const transitionText = this.add.text(width / 2, height / 2, '🌀', {
      fontSize: '64px',
    });
    transitionText.setOrigin(0.5);
    transitionText.setAlpha(0);
    transitionText.setDepth(200);

    // === ANIMATION SEQUENCE ===
    
    // Phase 1: Fade in overlay and zoom (0 - 0.5s)
    this.tweens.add({
      targets: overlay,
      alpha: 0.7,
      duration: 500,
      ease: 'Quad.easeIn',
    });

    this.tweens.add({
      targets: transitionText,
      alpha: 1,
      scale: 1.2,
      duration: 300,
      delay: 200,
    });

    // Phase 2: Squash the top-down view (simulates tilt) (0.5 - 1.5s)
    this.tweens.add({
      targets: topDownRoom,
      scaleY: 0.1,
      scaleX: 1.5,
      alpha: 0,
      duration: 800,
      delay: 500,
      ease: 'Quad.easeIn',
    });

    // Rotate the transition icon
    this.tweens.add({
      targets: transitionText,
      rotation: Math.PI * 2,
      duration: 1000,
      delay: 500,
      ease: 'Quad.easeInOut',
    });

    // Phase 3: Fade in side-view background (1.0 - 2.0s)
    this.tweens.add({
      targets: sideViewBg,
      alpha: 1,
      duration: 800,
      delay: 1000,
      ease: 'Quad.easeOut',
    });

    // Phase 4: Clear and transition to boss scene (2.0 - 2.5s)
    this.tweens.add({
      targets: [overlay, transitionText],
      alpha: 0,
      duration: 500,
      delay: 1800,
      ease: 'Quad.easeOut',
      onComplete: () => {
        // Start the boss scene
        this.time.delayedCall(200, () => {
          this.scene.start(this.transitionData.toScene, {
            fromTransition: true,
          });
        });
      },
    });
  }

  private createTopDownRepresentation(width: number, height: number): Phaser.GameObjects.Container {
    const container = this.add.container(width / 2, height / 2);

    // Simplified top-down room representation
    // Floor
    const floor = this.add.rectangle(0, 0, 300, 200, 0x55efc4, 0.8);
    container.add(floor);

    // Room border
    const border = this.add.graphics();
    border.lineStyle(4, 0x2d3436, 1);
    border.strokeRect(-150, -100, 300, 200);
    container.add(border);

    // Bed (target object)
    const bed = this.add.rectangle(0, 0, 80, 60, 0xff69b4, 1);
    container.add(bed);

    // Bed outline
    const bedBorder = this.add.graphics();
    bedBorder.lineStyle(2, 0x2d3436, 1);
    bedBorder.strokeRect(-40, -30, 80, 60);
    container.add(bedBorder);

    // Player dot
    const player = this.add.circle(-80, 40, 10, 0x1a3a5c);
    container.add(player);

    return container;
  }
}
